import { Outlet, Link, useParams, useLocation, useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LunarGridLogo } from '@/app/components/LunarGridLogo';
import { getProject, type Project } from '@/lib/api';
import {
  ArrowLeft,
  FileText,
  Zap,
  Sparkles,
  History,
  Database,
  Menu,
  X,
  LogOut,
} from 'lucide-react';
import { Toaster } from 'sonner';
import { ThemeToggle, ThemeNameToggle } from '@/app/components/ThemeToggle';

interface NavItem {
  segment: string;
  label: string;
  icon: React.ElementType;
  tertiary?: boolean;
}

const mainNavItems: NavItem[] = [
  { segment: 'templates', label: 'Templates', icon: FileText },
  { segment: 'generate', label: 'Generate', icon: Zap },
  { segment: 'quick-generate', label: 'Quick Generate', icon: Sparkles },
  { segment: 'logs', label: 'Logs', icon: History },
];

const secondaryNavItems: NavItem[] = [
  { segment: 'data', label: 'Data', icon: Database, tertiary: true },
];

function SidebarNav({
  projectId,
  items,
  currentPath,
  onNavigate,
}: {
  projectId: string;
  items: NavItem[];
  currentPath: string;
  onNavigate?: () => void;
}) {
  return (
    <div className="space-y-0.5">
      {items.map((item) => {
        const Icon = item.icon;
        const href = `/projects/${projectId}/${item.segment}`;
        const isActive =
          currentPath === href ||
          currentPath.startsWith(`${href}/`);

        return (
          <Link
            key={item.segment}
            to={href}
            onClick={onNavigate}
            className={`
              flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-semibold transition-all duration-150
              ${isActive
                ? 'bg-primary/10 text-primary border-l-2 border-primary pl-[10px] nav-active-glow'
                : item.tertiary
                ? 'text-muted-foreground/60 hover:text-muted-foreground hover:bg-sidebar-accent'
                : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent'
              }
            `}
          >
            <Icon className="size-4 shrink-0" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

function Sidebar({
  project,
  projectId,
  currentPath,
  onNavigate,
  onLogout,
}: {
  project: Project | null;
  projectId: string;
  currentPath: string;
  onNavigate?: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="flex flex-col h-full bg-sidebar border-r border-sidebar-border w-[220px] shrink-0 sidebar-glow">
      {/* Logo */}
      <div className="px-5 pt-5 pb-1">
        <LunarGridLogo size="sm" />
        <div className="sidebar-logo-line mt-2.5" />
      </div>

      {/* Back link */}
      <div className="px-4 pt-3 pb-2">
        <Link
          to="/"
          onClick={onNavigate}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3" />
          <span>All Projects</span>
        </Link>
      </div>

      {/* Project name */}
      <div className="px-4 pb-4 pt-1">
        <div className="text-[10px] font-medium text-muted-foreground/50 uppercase tracking-widest mb-1">
          Project
        </div>
        <div
          className="font-display font-semibold text-foreground text-sm leading-snug truncate"
          title={project?.name}
        >
          {project?.name ?? '…'}
        </div>
        {project?.source_table_name && (
          <div className="text-[11px] text-muted-foreground/60 mt-0.5 font-mono truncate">
            {project.source_table_name}
          </div>
        )}
      </div>

      <div className="mx-4 border-t border-sidebar-border mb-3" />

      {/* Main nav */}
      <nav className="flex-1 px-3 overflow-y-auto">
        <SidebarNav
          projectId={projectId}
          items={mainNavItems}
          currentPath={currentPath}
          onNavigate={onNavigate}
        />
      </nav>

      {/* Secondary nav */}
      <div className="px-3 pb-3">
        <div className="mx-0 border-t border-sidebar-border mb-3" />
        <SidebarNav
          projectId={projectId}
          items={secondaryNavItems}
          currentPath={currentPath}
          onNavigate={onNavigate}
        />
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-sidebar-border flex items-center justify-between">
        <LunarGridLogo size="xs" className="opacity-50" />
        <div className="flex items-center gap-1.5">
          <ThemeNameToggle />
          <ThemeToggle />
          <button
            onClick={onLogout}
            title="Log out"
            className="theme-toggle"
          >
            <LogOut className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!projectId) {
      navigate('/');
      return;
    }
    getProject(projectId).then(setProject).catch(() => navigate('/'));
  }, [projectId, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('lunar-grid-authenticated');
    navigate('/login');
  };

  if (!projectId) return null;

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex">
        <Sidebar
          project={project}
          projectId={projectId}
          currentPath={location.pathname}
          onLogout={handleLogout}
        />
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 h-14 bg-sidebar border-b border-sidebar-border">
        <Link to="/" className="flex items-center gap-2">
          <LunarGridLogo size="sm" />
        </Link>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground"
        >
          {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div
            className="fixed inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative flex z-50">
            <Sidebar
              project={project}
              projectId={projectId}
              currentPath={location.pathname}
              onNavigate={() => setMobileOpen(false)}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto lg:pt-0 pt-14">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              className="bg-card rounded-xl border border-border shadow-sm px-8 py-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
